import { computed, inject, Injectable } from '@angular/core';
import { OnDemandAvailabilityStore } from '../api/on-demand/on-demand-availability-store.service';
import { OnDemandSelectionStateService } from './on-demand-selection-state.service';
import { OnDemandRelationProductGroupStore } from '../api/on-demand/on-demand-relation-product-group-store.service';
import { EPriceType, ITariffSystemPayment } from '@web/dtos';
import { OnDemandComfortPaymentStore } from '../api/on-demand/on-demand-comfort-payment-store.service';

export function getComfortSurchargePrice(
  systemPayments: ITariffSystemPayment[]
): number {
  return systemPayments[0]?.price.priceIncludingVat ?? 0;
}

@Injectable({ providedIn: 'root' })
export class OnDemandPricingStore {
  private readonly availabilityStore = inject(OnDemandAvailabilityStore);
  private readonly selectionState = inject(OnDemandSelectionStateService);
  private readonly productGroupStore = inject(
    OnDemandRelationProductGroupStore
  );
  private readonly comfortPaymentStore: OnDemandComfortPaymentStore = inject(
    OnDemandComfortPaymentStore
  );

  private readonly operatorMapping = computed(
    () =>
      this.availabilityStore.matchedMapping() ??
      this.selectionState.checkoutOperatorMapping()
  );

  private readonly productGroups = computed(() => {
    const loadedProductGroups = this.productGroupStore.productGroups();
    return loadedProductGroups.length > 0
      ? loadedProductGroups
      : this.selectionState.checkoutProductGroups();
  });

  private readonly systemPayments = computed(() => {
    const loadedSystemPayments = this.comfortPaymentStore.systemPayments();
    return loadedSystemPayments.length > 0
      ? loadedSystemPayments
      : this.selectionState.checkoutSystemPayments();
  });

  private readonly showPublicTransportTickets = computed(() => {
    return this.productGroups().length > 0;
  });

  public getPrice(type: EPriceType, fahrgasttyp: string): number {
    if (type === EPriceType.PUBLIC_TRANSPORT) {
      if (!this.showPublicTransportTickets()) {
        return 0;
      }
      const mapping = this.operatorMapping();
      const groupName = mapping?.publicTransportProductGroup;
      const productGroups = this.productGroups();

      const targetGroup = productGroups.find(g => g.name === groupName);
      if (!targetGroup) {
        return 0;
      }

      const definition = targetGroup.productDefinitions?.find(
        def =>
          def.riderType === fahrgasttyp ||
          def.keyElements?.some(
            el => el.name === 'Fahrgasttyp' && el.value === fahrgasttyp
          )
      );
      return definition?.price?.priceIncludingVat ?? 0;
    }

    if (type === EPriceType.ON_DEMAND) {
      const option = this.selectionState.selectedConnectionOption();
      if (option && option.price !== undefined) {
        return Number(option.price);
      }
      return 0;
    }

    if (type === EPriceType.COMFORT) {
      return getComfortSurchargePrice(this.systemPayments());
    }
    return 0;
  }

  public getPassengerSinglePriceLabel(fahrgasttyp: string): string {
    const price = this.getPrice(EPriceType.PUBLIC_TRANSPORT, fahrgasttyp);
    return price.toLocaleString('de-AT', {
      style: 'currency',
      currency: 'EUR',
    });
  }

  public getPublicTransportTicketLabel(fahrgasttyp: string): string {
    const mapping = this.operatorMapping();
    const groupName = mapping?.publicTransportProductGroup;
    const productGroups = this.productGroups();

    const targetGroup = productGroups.find(g => g.name === groupName);
    const definition = targetGroup?.productDefinitions?.find(
      productDefinition =>
        productDefinition.riderType === fahrgasttyp ||
        productDefinition.keyElements?.some(
          element =>
            element.name === 'Fahrgasttyp' && element.value === fahrgasttyp
        )
    );

    return definition?.sort?.sortType ?? targetGroup?.name ?? '';
  }

  readonly totalAmount = computed(() => {
    let total = 0;
    const selections = this.selectionState.passengerTicketSelections() ?? [];

    for (const p of selections) {
      if (p.useExistingTicket) {
        total += this.getPrice(EPriceType.COMFORT, p.fahrgasttyp);
        continue;
      }

      total += this.getPrice(EPriceType.ON_DEMAND, p.fahrgasttyp);
      if (this.showPublicTransportTickets()) {
        total += this.getPrice(EPriceType.PUBLIC_TRANSPORT, p.fahrgasttyp);
      }
    }

    return total;
  });

  readonly totalAmountLabel = computed(() => {
    return this.totalAmount().toLocaleString('de-AT', {
      style: 'currency',
      currency: 'EUR',
    });
  });
}
